/**
 * Settings (tenant admins; help `cloud.settings`). Today one setting, the logo size (MB decision 524, Q463 point 7 B):
 * small, medium or large, or a height in px from 32 to 96, for the tenant's logo in Teslyn Field's top bar, the
 * portal header and the sign-in card. Kept as `logo_size` in `tenant.theme` and saved with tenant_set_logo_size
 * (20261025000200_tenant_logo_size.sql), which changes only that key. Remote support (MB decision 583): whether the
 * organisation's people may start "Get remote support" (Windows Quick Assist; the text says "the Teslyn
 * Support team" and names no tool, decision 646) in Teslyn Field, `tenant.remote_support` saved with
 * tenant_set_remote_support (20261026000700_kml_remote_support.sql) and synced to the laptops with the tenant row.
 * Public air traffic (Diurnal parity C7, decision 627; docs/specs/cloud/public-traffic.md): whether Teslyn Cloud asks
 * public air traffic around the projects in progress for the Operations Hub's map, off by default,
 * `cloud_traffic_setting.enabled` saved with cloud_traffic_set (20261027002000_cloud_public_traffic.sql).
 * The portal name, colours and custom domain follow in phase 6.
 */
import { useCallback, useEffect, useState, type FormEvent, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  ROADS_TILES_DEFAULT,
  checkRoadsTiles,
  roadsTiles,
  type RoadsTiles,
  TENANT_LOGO_MAX_PX,
  TENANT_LOGO_MIN_PX,
  TENANT_LOGO_PX,
  checkTenantLogoSize,
  tenantLogoHeightPx,
  tenantLogoSize,
  type TenantLogoSize,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { NumberField, TextField } from '../components/Fields.tsx';
import { setRoadsTiles } from '../map/roads.ts';
import { ErrorState, Loading, PageHeader, SectionHeader } from '../components/States.tsx';
import { applyLogoSize, type Tenant } from '../tenant/tenant.ts';

const PRESETS = ['small', 'medium', 'large'] as const;
const PRESET_LABEL: Record<(typeof PRESETS)[number], string> = {
  small: 'Small',
  medium: 'Medium',
  large: 'Large',
};

const sizeWords = (s: TenantLogoSize): string =>
  typeof s === 'number' ? `${s} px` : `${s} (${TENANT_LOGO_PX[s]} px)`;

async function loadLogoSize(
  sb: SupabaseClient,
  tenantId: string,
  fallback: TenantLogoSize,
): Promise<TenantLogoSize> {
  const { data, error } = await sb.from('tenant').select('id, theme').eq('id', tenantId).maybeSingle();
  if (error) throw new Error(error.message);
  return tenantLogoSize((data as { theme?: unknown } | null)?.theme, { logo_size: fallback });
}

async function saveLogoSize(sb: SupabaseClient, tenantId: string, size: TenantLogoSize): Promise<void> {
  const { data, error } = await sb.rpc('tenant_set_logo_size', { p_tenant: tenantId, p_size: size });
  if (error) throw new Error(error.message);
  if (data === null || data === undefined)
    throw new Error('Not saved: only a tenant admin of this organisation can change its logo size.');
}

/** The organisation's remote support switch; null while Teslyn Cloud does not have the column yet (not migrated). */
async function loadRemoteSupport(sb: SupabaseClient, tenantId: string): Promise<boolean | null> {
  const { data, error } = await sb
    .from('tenant')
    .select('id, remote_support')
    .eq('id', tenantId)
    .maybeSingle();
  if (error) {
    if (error.code === '42703' || /remote_support/.test(error.message)) return null;
    throw new Error(error.message);
  }
  return (data as { remote_support?: unknown } | null)?.remote_support !== false;
}

/** The organisation's public air traffic switch; null while Teslyn Cloud does not have the table yet. */
async function loadCloudTraffic(sb: SupabaseClient, tenantId: string): Promise<boolean | null> {
  const { data, error } = await sb
    .from('cloud_traffic_setting')
    .select('tenant_id, enabled')
    .eq('tenant_id', tenantId)
    .maybeSingle();
  if (error) {
    if (error.code === '42P01' || error.code === 'PGRST205' || /cloud_traffic_setting/.test(error.message))
      return null;
    throw new Error(error.message);
  }
  return (data as { enabled?: unknown } | null)?.enabled === true;
}

async function saveCloudTraffic(sb: SupabaseClient, tenantId: string, on: boolean): Promise<void> {
  const { data, error } = await sb.rpc('cloud_traffic_set', { p_tenant: tenantId, p_on: on });
  if (error) throw new Error(error.message);
  if (data === null || data === undefined)
    throw new Error('Not saved: only a tenant admin of this organisation can change public air traffic.');
}

async function saveRemoteSupport(sb: SupabaseClient, tenantId: string, on: boolean): Promise<void> {
  const { data, error } = await sb.rpc('tenant_set_remote_support', { p_tenant: tenantId, p_on: on });
  if (error) throw new Error(error.message);
  if (data === null || data === undefined)
    throw new Error('Not saved: only a tenant admin of this organisation can change remote support.');
}

export function SettingsScreen({
  sb,
  tenantId,
  roles,
  tenant,
  embedded = false,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly roles: readonly string[];
  readonly tenant: Tenant;
  /** Shown as Company's "Settings" tab (C6, decision 844): no page title of its own. */
  readonly embedded?: boolean;
}): ReactElement {
  const editable = roles.includes('tenant_admin');
  const [stored, setStored] = useState<TenantLogoSize | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(() => {
    setError(null);
    loadLogoSize(sb, tenantId, tenant.logoSize)
      .then(setStored)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, tenantId, tenant.logoSize]);
  useEffect(reload, [reload]);

  return (
    <section className={embedded ? 'stack' : 'page'}>
      {embedded ? (
        <SectionHeader title="Settings" subtitle={`How ${tenant.name}'s Teslyn looks and works`} />
      ) : (
        <PageHeader title="Settings" subtitle={`How ${tenant.name}'s Teslyn looks and works`} />
      )}
      {error && <ErrorState message={error} onRetry={reload} />}
      {!error && stored === null && <Loading what="your organisation's settings" />}
      {stored !== null && (
        <LogoSizePanel
          stored={stored}
          tenant={tenant}
          editable={editable}
          onSave={async (size) => {
            await saveLogoSize(sb, tenantId, size);
            applyLogoSize(size);
            setStored(size);
          }}
        />
      )}
      <RemoteSupportPanel sb={sb} tenantId={tenantId} tenantName={tenant.name} editable={editable} />
      <PublicTrafficPanel sb={sb} tenantId={tenantId} editable={editable} />
      <RoadsTilesPanel sb={sb} tenantId={tenantId} editable={editable} />
      <p className="caption muted">
        The portal name, brand colours with a contrast check, and your own web address follow in phase 6.
      </p>
    </section>
  );
}

function LogoSizePanel({
  stored,
  tenant,
  editable,
  onSave,
}: {
  readonly stored: TenantLogoSize;
  readonly tenant: Tenant;
  readonly editable: boolean;
  readonly onSave: (size: TenantLogoSize) => Promise<void>;
}): ReactElement {
  const [kind, setKind] = useState<(typeof PRESETS)[number] | 'px'>(
    typeof stored === 'number' ? 'px' : stored,
  );
  const [px, setPx] = useState<number | null>(tenantLogoHeightPx(stored));
  const [issue, setIssue] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const chosen: TenantLogoSize | null = kind === 'px' ? px : kind;
  const preview = chosen !== null && checkTenantLogoSize(chosen) === null ? tenantLogoHeightPx(chosen) : null;

  function submit(e: FormEvent): void {
    e.preventDefault();
    setSaved(null);
    const why = checkTenantLogoSize(chosen);
    if (why || chosen === null)
      return setIssue(why ?? `Enter a height from ${TENANT_LOGO_MIN_PX} to ${TENANT_LOGO_MAX_PX} px.`);
    setIssue(null);
    onSave(chosen)
      .then(() => setSaved(`Logo size saved: ${sizeWords(chosen)}.`))
      .catch((err: unknown) => setIssue(err instanceof Error ? err.message : String(err)));
  }

  return (
    <form className="panel stack" aria-label="Logo size" onSubmit={submit} noValidate>
      <h2 className="heading">Logo size</h2>
      <p className="body">
        How tall {tenant.name}'s logo stands in Teslyn Field's top bar, the portal header and the sign-in
        card. Medium suits a lockup with its own words; a phone shows three quarters of it.
      </p>
      {editable ? (
        <>
          <fieldset className="stack">
            <legend className="label muted">Size</legend>
            {PRESETS.map((p) => (
              <label key={p} className="row body">
                <input type="radio" name="logo-size" checked={kind === p} onChange={() => setKind(p)} />
                {`${PRESET_LABEL[p]} (${TENANT_LOGO_PX[p]} px)`}
              </label>
            ))}
            <label className="row body">
              <input type="radio" name="logo-size" checked={kind === 'px'} onChange={() => setKind('px')} />
              Height in px
            </label>
          </fieldset>
          {kind === 'px' && (
            <NumberField
              label="Height"
              unit="px"
              step="1"
              hint={`From ${TENANT_LOGO_MIN_PX} to ${TENANT_LOGO_MAX_PX} px`}
              value={px}
              onChange={setPx}
            />
          )}
        </>
      ) : (
        <p className="body">
          {`${sizeWords(stored)[0]!.toUpperCase()}${sizeWords(stored).slice(1)}. Your tenant admin sets the logo size.`}
        </p>
      )}
      {tenant.logo && preview !== null && (
        <div className="logo-preview">
          <img src={tenant.logo} alt={tenant.name} style={{ height: `${preview}px` }} />
        </div>
      )}
      {issue && (
        <p className="field__error caption" role="alert">
          {issue}
        </p>
      )}
      {saved && (
        <p className="notice" role="status">
          {saved}
        </p>
      )}
      {editable && (
        <div className="row">
          <Button type="submit" variant="primary">
            Save logo size
          </Button>
        </div>
      )}
    </form>
  );
}

function RemoteSupportPanel({
  sb,
  tenantId,
  tenantName,
  editable,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly tenantName: string;
  readonly editable: boolean;
}): ReactElement {
  // undefined: loading; null: Teslyn Cloud does not have the setting yet
  const [stored, setStored] = useState<boolean | null | undefined>(undefined);
  const [on, setOn] = useState(true);
  const [issue, setIssue] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    loadRemoteSupport(sb, tenantId)
      .then((v) => {
        if (!live) return;
        setStored(v);
        if (v !== null) setOn(v);
      })
      .catch((e: unknown) => live && setIssue(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [sb, tenantId]);

  function submit(e: FormEvent): void {
    e.preventDefault();
    setSaved(null);
    setIssue(null);
    saveRemoteSupport(sb, tenantId, on)
      .then(() => {
        setStored(on);
        setSaved(
          on
            ? 'Remote support is on: Teslyn Field offers "Get remote support" in Help.'
            : 'Remote support is off: Teslyn Field says it is turned off by your company.',
        );
      })
      .catch((err: unknown) => setIssue(err instanceof Error ? err.message : String(err)));
  }

  return (
    <form className="panel stack" aria-label="Remote support" onSubmit={submit} noValidate>
      <h2 className="heading">Remote support</h2>
      <p className="body">
        With "Get remote support" in Teslyn Field's Help, the Teslyn Support team can see the screen of a{' '}
        {tenantName} laptop, and with permission control it, using the remote help app that comes with
        Windows. The person at the laptop always starts it while on a phone call with the support team, types
        the code they give and presses Allow. Each start is written in your audit log. Turn it off if your IT
        policy does not allow remote help tools. Laptops pick up the change when they next sync.
      </p>
      {stored === undefined && !issue && <p className="caption muted">Loading…</p>}
      {stored === null && (
        <p className="caption muted">
          Remote support settings are not available yet: Teslyn Cloud has not been updated for them. Teslyn
          Field offers "Get remote support" meanwhile.
        </p>
      )}
      {stored !== undefined && stored !== null && editable && (
        <label className="row body">
          <input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} />
          Let our people start "Get remote support" in Teslyn Field
        </label>
      )}
      {stored !== undefined && stored !== null && !editable && (
        <p className="body">
          {stored
            ? 'On: Teslyn Field offers "Get remote support" in Help. Your tenant admin sets this.'
            : 'Off: Teslyn Field does not offer "Get remote support". Your tenant admin sets this.'}
        </p>
      )}
      {issue && (
        <p className="field__error caption" role="alert">
          {issue}
        </p>
      )}
      {saved && (
        <p className="notice" role="status">
          {saved}
        </p>
      )}
      {editable && stored !== undefined && stored !== null && (
        <div className="row">
          <Button type="submit" variant="primary">
            Save remote support
          </Button>
        </div>
      )}
    </form>
  );
}

function PublicTrafficPanel({
  sb,
  tenantId,
  editable,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly editable: boolean;
}): ReactElement {
  // undefined: loading; null: Teslyn Cloud does not have the setting yet
  const [stored, setStored] = useState<boolean | null | undefined>(undefined);
  const [on, setOn] = useState(false);
  const [issue, setIssue] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    loadCloudTraffic(sb, tenantId)
      .then((v) => {
        if (!live) return;
        setStored(v);
        if (v !== null) setOn(v);
      })
      .catch((e: unknown) => live && setIssue(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [sb, tenantId]);

  function submit(e: FormEvent): void {
    e.preventDefault();
    setSaved(null);
    setIssue(null);
    saveCloudTraffic(sb, tenantId, on)
      .then(() => {
        setStored(on);
        setSaved(
          on
            ? 'Public air traffic is on: the Operations Hub and project maps show it within a minute of opening.'
            : 'Public air traffic is off: Teslyn Cloud no longer asks for it, and the positions it kept are deleted.',
        );
      })
      .catch((err: unknown) => setIssue(err instanceof Error ? err.message : String(err)));
  }

  const ready = stored !== undefined && stored !== null;
  return (
    <form className="panel stack" aria-label="Public air traffic" onSubmit={submit} noValidate>
      <h2 className="heading">Public air traffic</h2>
      <p className="body">
        Teslyn Cloud can show other aircraft near your projects on the Operations Hub map and on project maps,
        from the community ADS-B network adsb.lol and the Oz Runways and AvPlan apps. While a staff member has
        one of those maps open, it asks at once about the area of each project that is mobilising or in
        progress, with 100 nautical miles around it, then keeps them current: adsb.lol every 5 minutes and Oz
        Runways and AvPlan every 2 minutes. It keeps the last 2 hours of positions, so each aircraft shows
        with its trail. Public air traffic is for your staff only: clients never see it. Teslyn Field shows
        public air traffic on each laptop as before, whatever is set here.
      </p>
      {stored === undefined && !issue && <p className="caption muted">Loading…</p>}
      {stored === null && (
        <p className="caption muted">
          Public air traffic is not available yet: Teslyn Cloud has not been updated for it.
        </p>
      )}
      {ready && editable && (
        <label className="row body">
          <input type="checkbox" checked={on} onChange={(e) => setOn(e.target.checked)} />
          Show public air traffic on the Operations Hub map
        </label>
      )}
      {ready && !editable && (
        <p className="body">
          {stored
            ? 'On: the Operations Hub map shows public air traffic. Your tenant admin sets this.'
            : 'Off: the Operations Hub map shows only your own aircraft. Your tenant admin sets this.'}
        </p>
      )}
      {issue && (
        <p className="field__error caption" role="alert">
          {issue}
        </p>
      )}
      {saved && (
        <p className="notice" role="status">
          {saved}
        </p>
      )}
      {editable && ready && (
        <div className="row">
          <Button type="submit" variant="primary">
            Save public air traffic
          </Button>
        </div>
      )}
    </form>
  );
}

/** N19 (decision 641): the organisation's Roads map tile address, from `tenant.theme.roads_tiles`. */
async function loadRoadsTiles(sb: SupabaseClient, tenantId: string): Promise<RoadsTiles> {
  const { data, error } = await sb.from('tenant').select('id, theme').eq('id', tenantId).maybeSingle();
  if (error) throw new Error(error.message);
  return roadsTiles((data as { theme?: unknown } | null)?.theme);
}

async function saveRoadsTiles(
  sb: SupabaseClient,
  tenantId: string,
  tiles: { template: string; attribution: string } | null,
): Promise<void> {
  const { data, error } = await sb.rpc('tenant_set_roads_tiles', { p_tenant: tenantId, p_tiles: tiles });
  if (error) throw new Error(error.message);
  if (data === null || data === undefined)
    throw new Error('Not saved: only a tenant admin of this organisation can change the Roads map.');
}

/**
 * "Roads map" (N19, decision 641): the tile address of the Roads base map in Teslyn Field and Teslyn Cloud, so the
 * organisation can move to a paid OpenStreetMap host without a new release. Tenant admins change it; empty is
 * OpenStreetMap's own tiles.
 */
function RoadsTilesPanel({
  sb,
  tenantId,
  editable,
}: {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly editable: boolean;
}): ReactElement {
  const [stored, setStored] = useState<RoadsTiles | null>(null);
  const [template, setTemplate] = useState('');
  const [attribution, setAttribution] = useState('');
  const [issue, setIssue] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    loadRoadsTiles(sb, tenantId)
      .then((r) => {
        if (!live) return;
        setStored(r);
        setTemplate(r.custom ? r.template : '');
        setAttribution(r.custom ? r.attribution : '');
      })
      .catch((e: unknown) => live && setIssue(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [sb, tenantId]);

  function done(r: RoadsTiles, words: string): void {
    setStored(r);
    setRoadsTiles(r);
    setSaved(words);
  }

  function submit(e: FormEvent): void {
    e.preventDefault();
    setSaved(null);
    const why = checkRoadsTiles({ template, attribution });
    if (why) return setIssue(why);
    setIssue(null);
    const tiles = { template: template.trim(), attribution: attribution.trim() };
    saveRoadsTiles(sb, tenantId, tiles)
      .then(() =>
        done(
          { ...tiles, copyrightUrl: null, custom: true },
          'Roads map saved: Teslyn Cloud uses it now, and Teslyn Field when each laptop next signs in or syncs.',
        ),
      )
      .catch((err: unknown) => setIssue(err instanceof Error ? err.message : String(err)));
  }

  function useOsm(): void {
    setSaved(null);
    setIssue(null);
    saveRoadsTiles(sb, tenantId, null)
      .then(() => {
        setTemplate('');
        setAttribution('');
        done(ROADS_TILES_DEFAULT, "Roads map saved: OpenStreetMap's own tiles.");
      })
      .catch((err: unknown) => setIssue(err instanceof Error ? err.message : String(err)));
  }

  return (
    <form className="panel stack" aria-label="Roads map" onSubmit={submit} noValidate>
      <h2 className="heading">Roads map</h2>
      <p className="body">
        Where the Roads base map's tiles come from, in Teslyn Field and Teslyn Cloud. Leave it empty for
        OpenStreetMap's own tiles, which suit light use. For heavy use, or to keep tiles for offline work,
        enter a paid OpenStreetMap host's address (MapTiler, Stadia, Thunderforest) with {'{z}'}, {'{x}'} and{' '}
        {'{y}'}, and the credit it asks for. Every staff member's laptop reads the address, so a key in it is
        not secret.
      </p>
      {stored === null && !issue && <p className="caption muted">Loading…</p>}
      {stored !== null && !editable && (
        <p className="body">
          {stored.custom
            ? `Tiles from ${new URL(stored.template.replace(/[{}]/g, '')).host}. Your tenant admin sets this.`
            : "OpenStreetMap's own tiles. Your tenant admin sets this."}
        </p>
      )}
      {stored !== null && editable && (
        <>
          <TextField
            label="Tile address"
            value={template}
            onChange={setTemplate}
            type="url"
            spellCheck={false}
            hint="For example https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}.png?key=…"
          />
          <TextField
            label="Credit on the map"
            value={attribution}
            onChange={setAttribution}
            hint="For example © MapTiler © OpenStreetMap contributors"
          />
        </>
      )}
      {issue && (
        <p className="field__error caption" role="alert">
          {issue}
        </p>
      )}
      {saved && (
        <p className="notice" role="status">
          {saved}
        </p>
      )}
      {editable && stored !== null && (
        <div className="row">
          <Button type="submit" variant="primary">
            Save Roads map
          </Button>
          {stored.custom && (
            <Button type="button" onClick={useOsm}>
              Use OpenStreetMap&apos;s own tiles
            </Button>
          )}
        </div>
      )}
    </form>
  );
}
